import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { CURRENCY_FRAGMENT } from "../../fragments/menu";

import type {
  MenuEditResp,
  MenuEditVariables,
  MenuVariables,
  Menu
} from "../../types/menuTypes";

const UPDATE_MENU = gql`
  mutation UpdateMenu($attributes: UpdateMenuInput!) {
    updateMenu(attributes: $attributes) {
      menu {
        id
        name
        description
        languages {
          position
          language
        }
        mainLanguage
        restaurantId
        ...CurrencyFragment
      }
      errors
    }
  }
  ${CURRENCY_FRAGMENT}
`   

type UseUpdateMenuOptions = {
  onCompleted?: (menu: Menu) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<MenuEditResp, MenuEditVariables>>[1];
};

const useUpdateMenu = (options?: UseUpdateMenuOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    MenuEditResp,
    MenuEditVariables
  >(UPDATE_MENU, options?.mutationOptions);

  const updateMenu = useCallback(
    async (input: MenuVariables & { id: string }) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              id: input.id!,
              name: input.name,
              languages: input.languages,
              mainLanguage: input.mainLanguage,
              description: input.description,
              restaurantId: input.restaurantId,
              currency: input.currency,
            },
          },
        });

        const updateMenu = result?.data?.updateMenu;
        
        if (updateMenu?.errors?.length) {
          throw new Error(updateMenu.errors.join(", "));
        }

        if (options?.onCompleted && updateMenu?.menu) {
          options.onCompleted(updateMenu.menu);
        }

        return updateMenu?.menu;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const menu = data?.updateMenu?.menu;

  return {
    updateMenu,
    updatedMenu: menu,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useUpdateMenu
